<script setup lang="ts">
import WrButton from '~/components/global/WrButton.vue';
import WrIcon from '~/components/global/WrIcon.vue';

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface StruggleItem {
  label: string;
  icon?: string;
  svgIcon?: string;
}

const emit = defineEmits<{ 'cta-clicked': []; }>();

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    struggles: StruggleItem[];
    ctaText?: string;
    ctaLink?: string | null;
    bgImageLight?: string;
    bgImageDark?: string;
    bgPosition?: string;
  }>(),
  {
    ctaText: "Let's Fix Your UX",
    ctaLink: null,
    bgImageLight: '/images/typical-uiux-struggles-bg-light.svg',
    bgImageDark: '/images/typical-uiux-struggles-bg-dark.svg',
    bgPosition: '50% -120px',
  }
);
</script>

<template>
  <section
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px] relative"
    :style="{
      backgroundImage: `url(${isLight ? bgImageLight : bgImageDark})`,
      backgroundSize: '120% auto',
      backgroundPosition: bgPosition,
      backgroundRepeat: 'no-repeat',
    }"
  >
    <div class="relative z-10 px-4">
      <!-- Heading -->
      <h2
        class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi"
        style="background: linear-gradient(92deg, #12DBFF -3.57%, #BA55FE 80.72%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;"
      >
        {{ heading }}
      </h2>
      <p
        class="text-center mt-4 px-4 text-[16px] lg:text-[18px] font-normal tracking-[0.36px] font-avant"
        :class="colorMode.preference === 'dark' ? 'text-[#D9D9D9]' : 'text-[#000000]'"
      >
        {{ subcopy }}
      </p>

      <!-- Scattered Cards -->
      <div class="mt-16 max-w-[900px] mx-auto">
        <!-- Desktop: scattered absolute layout | Mobile: stacked flex -->
        <div class="hidden lg:block relative min-h-[350px]">
          <!-- Row 1: 2 cards -->
          <div
            v-for="(item, idx) in struggles.slice(0, 2)"
            :key="idx"
            class="absolute flex items-center rounded-lg"
            :style="{
              padding: '8px 16px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '13px',
              borderRadius: '8px',
              border: '1px solid rgba(61, 27, 96, 0.07)',
              background: colorMode.preference === 'dark' ? 'linear-gradient(32deg, #1E0236 -1.26%, #390166 100.92%)' : 'linear-gradient(32deg, #FBF8F8 -1.26%, #FFF 100.92%)',
              ...(idx === 0 ? { top: '0%', left: '10%' } : { top: '0%', right: '10%' })
            }"
          >
            <div class="size-4 lg:size-6 flex-shrink-0">
              <slot :name="`icon-${idx}`" :index="idx">
                <WrIcon v-if="item.svgIcon || item.icon" :name="(item.svgIcon || item.icon)!" class="size-4 lg:size-6 text-black dark:text-white" />
              </slot>
            </div>
            <span :class="colorMode.preference === 'dark' ? 'text-white' : 'text-black'" class="font-avant text-[15px] font-normal">
              {{ item.label }}
            </span>
          </div>

          <!-- Row 2: 3 cards -->
          <div
            v-for="(item, idx) in struggles.slice(2, 5)"
            :key="idx + 2"
            class="absolute flex items-center rounded-lg"
            :style="{
              padding: '8px 16px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '13px',
              borderRadius: '8px',
              border: '1px solid rgba(61, 27, 96, 0.07)',
              background: colorMode.preference === 'dark' ? 'linear-gradient(32deg, #1E0236 -1.26%, #390166 100.92%)' : 'linear-gradient(32deg, #FBF8F8 -1.26%, #FFF 100.92%)',
              ...(idx === 0 ? { top: '28%', left: '0%' } :
              idx === 1 ? { top: '30%', left: '35%' } :
              { top: '28%', right: '0%' })
            }"
          >
            <div class="size-4 lg:size-6 flex-shrink-0">
              <slot :name="`icon-${idx + 2}`" :index="idx + 2">
                <WrIcon v-if="item.svgIcon || item.icon" :name="(item.svgIcon || item.icon)!" class="size-4 lg:size-6 text-black dark:text-white" />
              </slot>
            </div>
            <span :class="colorMode.preference === 'dark' ? 'text-white' : 'text-black'" class="font-avant text-[15px] font-normal">
              {{ item.label }}
            </span>
          </div>

          <!-- Row 3: 2 cards -->
          <div
            v-for="(item, idx) in struggles.slice(5, 7)"
            :key="idx + 5"
            class="absolute flex items-center rounded-lg"
            :style="{
              padding: '8px 16px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '13px',
              borderRadius: '8px',
              border: '1px solid rgba(61, 27, 96, 0.07)',
              background: colorMode.preference === 'dark' ? 'linear-gradient(32deg, #1E0236 -1.26%, #390166 100.92%)' : 'linear-gradient(32deg, #FBF8F8 -1.26%, #FFF 100.92%)',
              ...(idx === 0 ? { bottom: '22%', left: '15%' } : { bottom: '22%', right: '15%' })
            }"
          >
            <div class="size-4 lg:size-6 flex-shrink-0">
              <slot :name="`icon-${idx + 5}`" :index="idx + 5">
                <WrIcon v-if="item.svgIcon || item.icon" :name="(item.svgIcon || item.icon)!" class="size-4 lg:size-6 text-black dark:text-white" />
              </slot>
            </div>
            <span :class="colorMode.preference === 'dark' ? 'text-white' : 'text-black'" class="font-avant text-[15px] font-normal">
              {{ item.label }}
            </span>
          </div>
        </div>

        <!-- Mobile/Tablet: stacked grid layout -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:hidden">
          <div
            v-for="(item, idx) in struggles"
            :key="idx"
            class="flex items-center rounded-lg px-4 py-2"
            :style="{
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '13px',
              borderRadius: '8px',
              border: '1px solid rgba(61, 27, 96, 0.07)',
              background: colorMode.preference === 'dark' ? 'linear-gradient(32deg, #1E0236 -1.26%, #390166 100.92%)' : 'linear-gradient(32deg, #FBF8F8 -1.26%, #FFF 100.92%)',
            }"
          >
            <div class="size-4 lg:size-6 flex-shrink-0">
              <slot :name="`icon-${idx}`" :index="idx">
                <WrIcon v-if="item.svgIcon || item.icon" :name="(item.svgIcon || item.icon)!" class="size-4 lg:size-6 text-black dark:text-white" />
              </slot>
            </div>
            <span :class="colorMode.preference === 'dark' ? 'text-white' : 'text-black'" class="font-avant text-[14px] font-normal">
              {{ item.label }}
            </span>
          </div>
        </div>
      </div>

      <!-- CTA Button -->
      <div class="flex justify-center mt-6 lg:mt-12">
        <WrButton
          :link="ctaLink || undefined"
          @click="!ctaLink ? emit('cta-clicked') : undefined"
          container-class="max-w-max mx-auto"
          append-class="text-sm lg:text-base px-6 py-3"
        >
          {{ ctaText }}
        </WrButton>
      </div>
    </div>
  </section>
</template>
